import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import {
  Lock,
  Users,
  CheckCircle,
  XCircle,
  Download,
  Home,
  Loader2,
  LogOut,
  RefreshCw,
  Mail,
  GraduationCap,
  BookOpen,
  Upload,
  Video,
  FileText,
  Trash2,
  FolderOpen,
  Plus,
  Eye,
  UserCog
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL = "https://customer-assets.emergentagent.com/job_academia-sabeneval/artifacts/ai0i1vi4_2F29CCC3-E812-45AF-8948-674464C4314C.PNG";

export default function AdminPage() {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [logging, setLogging] = useState(false);
  
  // Tab state
  const [activeTab, setActiveTab] = useState("inscripciones");
  
  // Enrollments
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(null);
  
  // Content management
  const [modules, setModules] = useState([]);
  const [selectedModule, setSelectedModule] = useState(null);
  const [moduleContent, setModuleContent] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  
  // Professors
  const [professors, setProfessors] = useState([]);
  const [newProfessor, setNewProfessor] = useState({ name: "", email: "", password: "", specialty: "" });
  const [addingProf, setAddingProf] = useState(false);
  
  // File refs
  const videoInputRef = useRef(null);
  const pdfInputRef = useRef(null);
  
  // Upload titles
  const [videoTitle, setVideoTitle] = useState("");
  const [pdfTitle, setPdfTitle] = useState("");

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!password) {
      toast.error("Por favor ingresa la contraseña");
      return;
    }
    
    setLogging(true);
    try {
      const response = await axios.post(`${API}/admin/login`, { password });
      if (response.data.success) {
        setIsAuthenticated(true);
        sessionStorage.setItem("adminAuth", "true");
        toast.success("Acceso concedido");
        fetchEnrollments();
        fetchModules();
        fetchProfessors();
      }
    } catch (error) {
      toast.error("Contraseña incorrecta");
    } finally {
      setLogging(false);
    }
  };

  const fetchEnrollments = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/enrollments`);
      setEnrollments(response.data);
    } catch (error) {
      console.error("Error fetching enrollments:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchModules = async () => {
    try {
      const response = await axios.get(`${API}/modules`);
      setModules(response.data.modules);
    } catch (error) {
      console.error("Error fetching modules:", error);
    }
  };

  const fetchProfessors = async () => {
    try {
      const response = await axios.get(`${API}/admin/professors`);
      setProfessors(response.data.professors || []);
    } catch (error) {
      console.error("Error fetching professors:", error);
    }
  };

  const fetchModuleContent = async (moduleId) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/modules/${moduleId}/content`);
      setModuleContent(response.data);
      setSelectedModule(response.data);
    } catch (error) {
      toast.error("Error al cargar contenido");
    } finally {
      setLoading(false);
    }
  };

  const togglePaymentStatus = async (enrollmentId, currentStatus) => {
    setUpdating(enrollmentId);
    try {
      await axios.put(`${API}/admin/enrollments/${enrollmentId}`, {
        payment_confirmed: !currentStatus
      });
      toast.success("Estado actualizado");
      fetchEnrollments();
    } catch (error) {
      toast.error("Error al actualizar estado");
    } finally {
      setUpdating(null);
    }
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!selectedModule) {
      toast.error("Selecciona un módulo primero");
      return;
    }
    
    if (!videoTitle.trim()) {
      toast.error("Ingresa un título para el video");
      return;
    }
    
    const formData = new FormData();
    formData.append("file", file);
    formData.append("module_id", selectedModule.id);
    formData.append("title", videoTitle);
    
    setUploading(true);
    setUploadProgress(0);
    
    try {
      await axios.post(`${API}/admin/upload/video`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });
      toast.success("Video subido exitosamente");
      setVideoTitle("");
      fetchModuleContent(selectedModule.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al subir video");
    } finally {
      setUploading(false);
      setUploadProgress(0);
      if (videoInputRef.current) videoInputRef.current.value = "";
    }
  };

  const handlePdfUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!selectedModule) {
      toast.error("Selecciona un módulo primero");
      return;
    }
    
    if (!pdfTitle.trim()) {
      toast.error("Ingresa un título para el PDF");
      return;
    }
    
    const formData = new FormData();
    formData.append("file", file);
    formData.append("module_id", selectedModule.id);
    formData.append("title", pdfTitle);
    
    setUploading(true);
    setUploadProgress(0);
    
    try {
      await axios.post(`${API}/admin/upload/pdf`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });
      toast.success("PDF subido exitosamente");
      setPdfTitle("");
      fetchModuleContent(selectedModule.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al subir PDF");
    } finally {
      setUploading(false);
      setUploadProgress(0);
      if (pdfInputRef.current) pdfInputRef.current.value = "";
    }
  };

  const deleteVideo = async (videoId) => {
    if (!confirm("¿Eliminar este video?")) return;
    try {
      await axios.delete(`${API}/admin/upload/video/${videoId}`);
      toast.success("Video eliminado");
      fetchModuleContent(selectedModule.id);
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  const deletePdf = async (fileId) => {
    if (!confirm("¿Eliminar este archivo?")) return;
    try {
      await axios.delete(`${API}/admin/upload/pdf/${fileId}`);
      toast.success("Archivo eliminado");
      fetchModuleContent(selectedModule.id);
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  const addProfessor = async () => {
    if (!newProfessor.name || !newProfessor.email || !newProfessor.password) {
      toast.error("Completa todos los campos obligatorios");
      return;
    }
    
    setAddingProf(true);
    try {
      await axios.post(`${API}/admin/professors`, newProfessor);
      toast.success("Profesor agregado");
      setNewProfessor({ name: "", email: "", password: "", specialty: "" });
      fetchProfessors();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al agregar profesor");
    } finally {
      setAddingProf(false);
    }
  };

  const deleteProfessor = async (profId) => {
    if (!confirm("¿Eliminar este profesor?")) return;
    try {
      await axios.delete(`${API}/admin/professors/${profId}`);
      toast.success("Profesor eliminado");
      fetchProfessors();
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  const exportToCSV = () => {
    if (enrollments.length === 0) {
      toast.error("No hay datos para exportar");
      return;
    }

    const headers = ["Nombre", "Email", "Universidad", "Carrera", "Mensaje", "Estado", "Pago Confirmado", "Fecha"];
    const rows = enrollments.map(e => [
      e.full_name,
      e.email,
      e.university,
      e.career,
      e.message || "",
      e.status,
      e.payment_confirmed ? "Sí" : "No",
      new Date(e.timestamp).toLocaleDateString('es-MX')
    ]);

    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${cell}"`).join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `inscripciones_sabeneval_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    
    toast.success("Archivo CSV descargado");
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem("adminAuth");
    setPassword("");
  };

  useEffect(() => {
    const auth = sessionStorage.getItem("adminAuth");
    if (auth === "true") {
      setIsAuthenticated(true);
      fetchEnrollments();
      fetchModules();
      fetchProfessors();
    }
  }, []);

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-lg" data-testid="admin-login-card">
          <CardHeader className="text-center pb-2">
            <img src={LOGO_URL} alt="Sabeneval" className="h-12 w-auto mx-auto mb-4" />
            <CardTitle className="font-heading text-2xl">Panel de Administración</CardTitle>
            <p className="text-slate-500 text-sm mt-2">Ingresa la contraseña para acceder</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password" className="flex items-center gap-2">
                  <Lock className="w-4 h-4 text-slate-400" />
                  Contraseña
                </Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Ingresa la contraseña"
                  className="h-11"
                  data-testid="admin-password-input"
                />
              </div>
              <Button
                type="submit"
                disabled={logging}
                className="w-full bg-purple-600 hover:bg-purple-700 text-white h-11"
                data-testid="admin-login-btn"
              >
                {logging ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Lock className="w-4 h-4 mr-2" />}
                {logging ? "Verificando..." : "Acceder"}
              </Button>
            </form>
            <div className="mt-4 text-center">
              <Button variant="ghost" onClick={() => navigate("/")} className="text-slate-500">
                <Home className="w-4 h-4 mr-2" />
                Volver al inicio
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const stats = {
    total: enrollments.length,
    confirmed: enrollments.filter(e => e.payment_confirmed).length,
    pending: enrollments.filter(e => !e.payment_confirmed).length
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-100 py-4 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <a href="/" className="flex items-center gap-2">
              <img src={LOGO_URL} alt="Sabeneval" className="h-10 w-auto" />
              <span className="font-heading font-bold text-lg text-purple-700 hidden sm:block">SABENEVAL</span>
            </a>
            <Badge className="bg-purple-100 text-purple-700">Panel Admin</Badge>
          </div>
          <div className="flex items-center gap-3">
            <Button onClick={() => navigate("/")} variant="ghost" className="text-slate-600">
              <Home className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">Inicio</span>
            </Button>
            <Button onClick={handleLogout} variant="outline" className="text-red-600 border-red-200">
              <LogOut className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">Salir</span>
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-slate-200 pb-2">
          <Button
            variant={activeTab === "inscripciones" ? "default" : "ghost"}
            onClick={() => setActiveTab("inscripciones")}
            className={activeTab === "inscripciones" ? "bg-purple-600" : ""}
            data-testid="tab-inscripciones"
          >
            <Users className="w-4 h-4 mr-2" />
            Inscripciones
          </Button>
          <Button
            variant={activeTab === "contenido" ? "default" : "ghost"}
            onClick={() => setActiveTab("contenido")}
            className={activeTab === "contenido" ? "bg-purple-600" : ""}
            data-testid="tab-contenido"
          >
            <FolderOpen className="w-4 h-4 mr-2" />
            Contenido
          </Button>
          <Button
            variant={activeTab === "profesores" ? "default" : "ghost"}
            onClick={() => setActiveTab("profesores")}
            className={activeTab === "profesores" ? "bg-purple-600" : ""}
            data-testid="tab-profesores"
          >
            <UserCog className="w-4 h-4 mr-2" />
            Profesores
          </Button>
        </div>

        {/* Inscripciones Tab */}
        {activeTab === "inscripciones" && (
          <>
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                      <Users className="w-6 h-6 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Total Inscripciones</p>
                      <p className="text-3xl font-bold text-slate-900">{stats.total}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                      <CheckCircle className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Pagos Confirmados</p>
                      <p className="text-3xl font-bold text-green-600">{stats.confirmed}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center">
                      <XCircle className="w-6 h-6 text-yellow-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Pagos Pendientes</p>
                      <p className="text-3xl font-bold text-yellow-600">{stats.pending}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-xl font-semibold">Lista de Inscripciones</h2>
              <div className="flex gap-2">
                <Button onClick={fetchEnrollments} variant="outline" disabled={loading}>
                  <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
                  Actualizar
                </Button>
                <Button onClick={exportToCSV} variant="outline">
                  <Download className="w-4 h-4 mr-2" />
                  CSV
                </Button>
              </div>
            </div>

            {/* Enrollments Table */}
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Nombre</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Email</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Universidad</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Carrera</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Estado</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Acción</th>
                      </tr>
                    </thead>
                    <tbody>
                      {enrollments.map((e) => (
                        <tr key={e.id} className="border-b hover:bg-slate-50">
                          <td className="px-4 py-3 font-medium">{e.full_name}</td>
                          <td className="px-4 py-3 text-slate-600">{e.email}</td>
                          <td className="px-4 py-3 text-slate-600">{e.university}</td>
                          <td className="px-4 py-3 text-slate-600">{e.career}</td>
                          <td className="px-4 py-3">
                            <Badge className={e.payment_confirmed ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}>
                              {e.payment_confirmed ? "Pagado" : "Pendiente"}
                            </Badge>
                          </td>
                          <td className="px-4 py-3">
                            <Button
                              size="sm"
                              variant={e.payment_confirmed ? "outline" : "default"}
                              onClick={() => togglePaymentStatus(e.id, e.payment_confirmed)}
                              disabled={updating === e.id}
                              className={!e.payment_confirmed ? "bg-green-600 hover:bg-green-700" : ""}
                            >
                              {updating === e.id ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                                e.payment_confirmed ? "Desmarcar" : "Confirmar Pago"
                              )}
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {/* Contenido Tab */}
        {activeTab === "contenido" && (
          <div className="grid md:grid-cols-4 gap-6">
            {/* Sidebar - Modules */}
            <div className="md:col-span-1">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg">Módulos</CardTitle>
                </CardHeader>
                <CardContent className="p-2">
                  <div className="space-y-1">
                    {modules.map((module) => (
                      <button
                        key={module.id}
                        onClick={() => fetchModuleContent(module.id)}
                        className={`w-full flex items-center gap-3 p-3 rounded-lg text-left transition-colors ${
                          selectedModule?.id === module.id ? "bg-purple-100 text-purple-700" : "hover:bg-slate-100"
                        }`}
                        data-testid={`content-module-${module.id}`}
                      >
                        <BookOpen className="w-5 h-5 flex-shrink-0" />
                        <span className="text-sm font-medium truncate">{module.title}</span>
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Main Content */}
            <div className="md:col-span-3">
              {!selectedModule ? (
                <Card>
                  <CardContent className="p-12 text-center">
                    <FolderOpen className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-slate-900 mb-2">Selecciona un módulo</h3>
                    <p className="text-slate-500">Elige un módulo para gestionar su contenido (videos y PDFs)</p>
                  </CardContent>
                </Card>
              ) : loading ? (
                <Card>
                  <CardContent className="p-12 text-center">
                    <Loader2 className="w-8 h-8 text-purple-600 animate-spin mx-auto" />
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-6">
                  {/* Module Header */}
                  <Card className="bg-gradient-to-r from-purple-600 to-purple-700 text-white">
                    <CardContent className="p-6">
                      <Badge className="bg-white/20 text-white mb-2">Módulo {selectedModule.id}</Badge>
                      <h2 className="font-heading text-2xl font-bold">{selectedModule.title}</h2>
                      <p className="text-purple-100 mt-2">{selectedModule.description}</p>
                    </CardContent>
                  </Card>

                  {/* Upload Progress */}
                  {uploading && (
                    <Card>
                      <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                          <Loader2 className="w-5 h-5 animate-spin text-purple-600" />
                          <div className="flex-1">
                            <p className="text-sm font-medium mb-1">Subiendo archivo...</p>
                            <Progress value={uploadProgress} className="h-2" />
                          </div>
                          <span className="text-sm text-slate-600">{uploadProgress}%</span>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Upload Videos */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Video className="w-5 h-5 text-blue-600" />
                        Videos ({moduleContent?.content?.videos?.length || 0})
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {/* Existing Videos */}
                      {moduleContent?.content?.videos?.length > 0 && (
                        <div className="space-y-2 mb-4">
                          {moduleContent.content.videos.map((video) => (
                            <div key={video.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                              <div className="flex items-center gap-3">
                                <Video className="w-5 h-5 text-blue-500" />
                                <div>
                                  <p className="font-medium">{video.title}</p>
                                  <p className="text-sm text-slate-500">
                                    {video.original_filename || video.url}
                                    {video.size_bytes && ` - ${(video.size_bytes / 1024 / 1024).toFixed(1)} MB`}
                                  </p>
                                </div>
                              </div>
                              <div className="flex gap-2">
                                {video.url && (
                                  <Button size="sm" variant="ghost" asChild>
                                    <a href={`${BACKEND_URL}${video.url}`} target="_blank" rel="noopener noreferrer">
                                      <Eye className="w-4 h-4" />
                                    </a>
                                  </Button>
                                )}
                                <Button size="sm" variant="ghost" className="text-red-600" onClick={() => deleteVideo(video.id)}>
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {/* Upload Form */}
                      <div className="border-t pt-4">
                        <p className="text-sm font-medium text-slate-700 mb-3">Subir nuevo video (MP4, WebM, MOV - máx 500MB)</p>
                        <div className="flex gap-3">
                          <Input
                            placeholder="Título del video"
                            value={videoTitle}
                            onChange={(e) => setVideoTitle(e.target.value)}
                            className="flex-1"
                          />
                          <input
                            ref={videoInputRef}
                            type="file"
                            accept="video/mp4,video/webm,video/mov,.mp4,.webm,.mov"
                            onChange={handleVideoUpload}
                            className="hidden"
                            id="video-upload"
                          />
                          <Button
                            onClick={() => videoInputRef.current?.click()}
                            disabled={uploading || !videoTitle.trim()}
                            className="bg-blue-600 hover:bg-blue-700"
                          >
                            <Upload className="w-4 h-4 mr-2" />
                            Subir Video
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Upload PDFs */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <FileText className="w-5 h-5 text-orange-600" />
                        PDFs ({moduleContent?.content?.files?.length || 0})
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {/* Existing PDFs */}
                      {moduleContent?.content?.files?.length > 0 && (
                        <div className="space-y-2 mb-4">
                          {moduleContent.content.files.map((file) => (
                            <div key={file.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                              <div className="flex items-center gap-3">
                                <FileText className="w-5 h-5 text-orange-500" />
                                <div>
                                  <p className="font-medium">{file.title}</p>
                                  <p className="text-sm text-slate-500">
                                    {file.original_filename || file.file_url}
                                    {file.size_bytes && ` - ${(file.size_bytes / 1024 / 1024).toFixed(1)} MB`}
                                  </p>
                                </div>
                              </div>
                              <div className="flex gap-2">
                                {file.file_url && (
                                  <Button size="sm" variant="ghost" asChild>
                                    <a href={`${BACKEND_URL}${file.file_url}`} target="_blank" rel="noopener noreferrer">
                                      <Eye className="w-4 h-4" />
                                    </a>
                                  </Button>
                                )}
                                <Button size="sm" variant="ghost" className="text-red-600" onClick={() => deletePdf(file.id)}>
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {/* Upload Form */}
                      <div className="border-t pt-4">
                        <p className="text-sm font-medium text-slate-700 mb-3">Subir nuevo PDF (máx 50MB)</p>
                        <div className="flex gap-3">
                          <Input
                            placeholder="Título del documento"
                            value={pdfTitle}
                            onChange={(e) => setPdfTitle(e.target.value)}
                            className="flex-1"
                          />
                          <input
                            ref={pdfInputRef}
                            type="file"
                            accept="application/pdf,.pdf"
                            onChange={handlePdfUpload}
                            className="hidden"
                            id="pdf-upload"
                          />
                          <Button
                            onClick={() => pdfInputRef.current?.click()}
                            disabled={uploading || !pdfTitle.trim()}
                            className="bg-orange-600 hover:bg-orange-700"
                          >
                            <Upload className="w-4 h-4 mr-2" />
                            Subir PDF
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Profesores Tab */}
        {activeTab === "profesores" && (
          <div className="grid md:grid-cols-2 gap-6">
            {/* Add Professor */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Plus className="w-5 h-5" />
                  Agregar Profesor
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Nombre completo *</Label>
                  <Input
                    value={newProfessor.name}
                    onChange={(e) => setNewProfessor({ ...newProfessor, name: e.target.value })}
                    placeholder="Nombre del profesor"
                  />
                </div>
                <div>
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    value={newProfessor.email}
                    onChange={(e) => setNewProfessor({ ...newProfessor, email: e.target.value })}
                    placeholder="profesor@email.com"
                  />
                </div>
                <div>
                  <Label>Contraseña *</Label>
                  <Input
                    type="password"
                    value={newProfessor.password}
                    onChange={(e) => setNewProfessor({ ...newProfessor, password: e.target.value })}
                    placeholder="Contraseña de acceso"
                  />
                </div>
                <div>
                  <Label>Especialidad</Label>
                  <Input
                    value={newProfessor.specialty}
                    onChange={(e) => setNewProfessor({ ...newProfessor, specialty: e.target.value })}
                    placeholder="Ej: Matemáticas"
                  />
                </div>
                <Button onClick={addProfessor} disabled={addingProf} className="w-full bg-purple-600 hover:bg-purple-700">
                  {addingProf ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                  Agregar Profesor
                </Button>
              </CardContent>
            </Card>

            {/* Professors List */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <UserCog className="w-5 h-5" />
                  Profesores Registrados ({professors.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {professors.length === 0 ? (
                  <p className="text-slate-500 text-center py-8">No hay profesores registrados</p>
                ) : (
                  <div className="space-y-2">
                    {professors.map((prof) => (
                      <div key={prof.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div>
                          <p className="font-medium">{prof.name}</p>
                          <p className="text-sm text-slate-500">{prof.email}</p>
                          {prof.specialty && <Badge variant="secondary" className="mt-1">{prof.specialty}</Badge>}
                        </div>
                        <Button size="sm" variant="ghost" className="text-red-600" onClick={() => deleteProfessor(prof.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
